import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-sm border border-brandborder bg-white px-3 py-2 text-navy text-sm outline-none focus:border-brandblue transition-colors";

function PackageEditor({ pkg, onSaved }) {
  const [form, setForm] = useState({ ...pkg, features: pkg.features.join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name, price: form.price, price_note: form.price_note,
        tagline: form.tagline, cta: form.cta, recommended: form.recommended,
        features: form.features.split("\n").map((x) => x.trim()).filter(Boolean),
      };
      const { data } = await api.put(`/packages/${pkg.slug}`, payload);
      toast.success(`${data.name} saved.`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-brandborder rounded-md p-6" data-testid={`edit-package-${pkg.slug}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-lg text-navy">{pkg.name}</h3>
        <label className="flex items-center gap-2 text-sm text-slate">
          <input type="checkbox" checked={form.recommended} onChange={(e) => set("recommended", e.target.checked)} className="w-4 h-4 accent-[#0968F0]" data-testid={`pkg-recommended-${pkg.slug}`} /> Recommended
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-slate">Name</label><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={`pkg-name-${pkg.slug}`} /></div>
        <div><label className="text-xs text-slate">Price</label><input className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid={`pkg-price-${pkg.slug}`} /></div>
      </div>
      <div className="mt-3"><label className="text-xs text-slate">Price note</label><input className={inputCls} value={form.price_note} onChange={(e) => set("price_note", e.target.value)} /></div>
      <div className="mt-3"><label className="text-xs text-slate">Tagline</label><input className={inputCls} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></div>
      <div className="mt-3"><label className="text-xs text-slate">Features (one per line)</label><textarea className={`${inputCls} min-h-[120px]`} value={form.features} onChange={(e) => set("features", e.target.value)} data-testid={`pkg-features-${pkg.slug}`} /></div>
      <div className="mt-3"><label className="text-xs text-slate">CTA label</label><input className={inputCls} value={form.cta} onChange={(e) => set("cta", e.target.value)} /></div>
      <button onClick={save} disabled={busy} data-testid={`pkg-save-${pkg.slug}`} className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50">
        <Save size={15} /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

function ServiceEditor({ svc, onSaved }) {
  const [form, setForm] = useState({ name: svc.name, pillar: svc.pillar, summary: svc.summary });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/services/${svc.slug}`, form);
      toast.success(`${data.name} saved.`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-brandborder rounded-md p-5" data-testid={`edit-service-${svc.slug}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-xs text-slate">Name</label><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={`svc-name-${svc.slug}`} /></div>
        <div><label className="text-xs text-slate">Pillar</label><input className={inputCls} value={form.pillar} onChange={(e) => set("pillar", e.target.value)} /></div>
      </div>
      <div className="mt-3"><label className="text-xs text-slate">Summary</label><textarea className={`${inputCls} min-h-[70px]`} value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid={`svc-summary-${svc.slug}`} /></div>
      <button onClick={save} disabled={busy} data-testid={`svc-save-${svc.slug}`} className="mt-3 inline-flex items-center gap-2 bg-navy text-white font-semibold text-sm px-4 py-2 rounded-sm hover:bg-brandblue transition-colors disabled:opacity-50">
        <Save size={14} /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

export default function AdminContent() {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("packages");

  const load = () => {
    api.get("/packages").then(({ data }) => setPackages(data));
    api.get("/services").then(({ data }) => setServices(data));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="page-admin-content">
      <h1 className="font-heading font-extrabold text-3xl text-navy">Packages & Services</h1>
      <p className="text-slate text-sm mt-1">Edit public package pricing and service copy. Changes are live immediately.</p>

      <div className="mt-6 flex gap-2 border-b border-brandborder">
        {["packages", "services"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-4 py-2.5 text-sm font-semibold capitalize border-b-2 transition-colors ${tab === t ? "border-brandblue text-brandblue" : "border-transparent text-slate hover:text-navy"}`}>{t}</button>
        ))}
      </div>

      {tab === "packages" ? (
        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          {packages.map((p) => <PackageEditor key={p.slug} pkg={p} onSaved={load} />)}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {services.map((s) => <ServiceEditor key={s.slug} svc={s} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
